import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  ArrowRightIcon, 
  CodeBracketIcon,
  ServerIcon,
  BoltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CloudIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { SiteFooter } from '@/components/layouts/SiteFooter'

export function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="relative bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/favicon-server.svg" 
                alt="OpenApps Logo" 
                className="w-8 h-8"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                OpenApps
              </span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#docs" className="text-gray-600 hover:text-gray-900 transition-colors">Docs</a>
              <a href="#support" className="text-gray-600 hover:text-gray-900 transition-colors">Support</a>
            </nav>

            <div className="hidden md:flex items-center space-x-4">
              <a 
                href="https://form.typeform.com/to/wbXoD5po" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Talk to Founder
              </a>
              <a 
                href="https://form.typeform.com/to/wbXoD5po"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors inline-block"
              >
                Get Early Access
              </a>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`bg-gray-600 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'}`}></span>
                <span className={`bg-gray-600 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`bg-gray-600 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-6 space-y-4">
              <a href="#features" className="block text-gray-600 hover:text-gray-900">Features</a>
              <a href="#docs" className="block text-gray-600 hover:text-gray-900">Docs</a>
              <a href="#support" className="block text-gray-600 hover:text-gray-900">Support</a>
              <div className="pt-4 space-y-2">
                <a href="https://form.typeform.com/to/wbXoD5po" target="_blank" rel="noopener noreferrer" className="block text-gray-600 hover:text-gray-900">Talk to Founder</a>
                <a href="https://form.typeform.com/to/wbXoD5po" target="_blank" rel="noopener noreferrer" className="block bg-black text-white px-4 py-2 rounded-lg text-center w-full">Get Early Access</a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-sm font-medium mb-8">
                <StarIcon className="w-4 h-4 mr-2" />
                🚀 Early Access Available
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Unlock your organization's{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                AGI
                </span>
                {' '}
              </h1>
              
              <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              Removing the stress and complexities of connecting massive web infrastructures to your AI Agents, so that your can focus on what really matters to you.
              {/* Eliminate the development, deployment and maintenance overhead, and turn your AI agents from thinkers into doers. */}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="https://form.typeform.com/to/wbXoD5po"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-800 transition-colors inline-flex items-center"
                >
                  Get Early Access
                  <ArrowRightIcon className="w-5 h-5 ml-2" />
                </a>
                <a 
                  href="https://form.typeform.com/to/wbXoD5po"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-700 px-8 py-4 rounded-lg text-lg font-medium border border-gray-300 hover:border-gray-400 transition-colors inline-flex items-center"
                >
                  Talk to Founder
                </a>
              </div>
              
              <div className="mt-12 text-sm text-gray-500">
                No credit card required • Deploy in under 1 minutes • Enterprise ready
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Background decoration */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-blue-100/40 to-purple-100/40 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to integrate APIs with AI
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From Web2.0 specs to production-ready Smart MCP & A2A servers in minutes, not weeks.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <motion.div 
              className="p-8 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <CodeBracketIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Instant Conversion</h3>
              <p className="text-gray-600">
                We're building a system that will convert your Web2.0 spec into a fully functional MCP server instantly. 
                Zero configuration, zero code changes.
              </p>
            </motion.div>

            <motion.div 
              className="p-8 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <BoltIcon className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Auto-scaling Infrastructure</h3>
              <p className="text-gray-600">
                We're designing enterprise-grade infrastructure that will automatically scale with your usage. 
                From prototypes to production, seamlessly.
              </p>
            </motion.div>

            <motion.div 
              className="p-8 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheckIcon className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Enterprise Security</h3>
              <p className="text-gray-600">
                Security-first design with OAuth2, API keys, rate limiting, and monitoring. 
                Built to meet enterprise compliance standards from day one.
              </p>
            </motion.div>

            <motion.div 
              className="p-8 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <ChartBarIcon className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Real-time Analytics</h3>
              <p className="text-gray-600">
                Monitor usage, performance, and errors in real-time. 
                Detailed insights into your MCP server performance.
              </p>
            </motion.div>

            <motion.div 
              className="p-8 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <CloudIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Multi-Protocol Support</h3>
              <p className="text-gray-600">
                Support for SSE, WebSocket, and HTTP transports. 
                Compatible with all major AI platforms and frameworks.
              </p>
            </motion.div>

            <motion.div 
              className="p-8 rounded-2xl border border-gray-200 hover:border-gray-300 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <ServerIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Team Collaboration</h3>
              <p className="text-gray-600">
                Share servers, manage permissions, and collaborate with your team. 
                Built for modern development workflows.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              From API to AI in three simple steps
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              No complex setup, no learning curve. Just upload and deploy.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Upload Web2.0 Spec</h3>
              <p className="text-gray-600">
                Simply upload your OpenAPI specification file or provide a URL. 
                Our platform will support OpenAPI 2.0, 3.0, and 3.1. And upcoming support for postman collections, Words docs and more.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Auto-Generate MCP or A2A Server</h3>
              <p className="text-gray-600">
                Our platform will automatically convert your API endpoints into MCP tools or A2A servers, 
                handling authentication, validation, and error handling intelligently.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Deploy & Connect</h3>
              <p className="text-gray-600">
                Your MCP server will be instantly deployed and ready to connect to AI platforms like OpenAI, Claude, and more.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Avoid all the headaches and pitfalls of building and scaling your own MCP or A2A server
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Built by developer for developer, everything you need to build high quality agentic tools.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://form.typeform.com/to/wbXoD5po"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center"
            >
              Get Early Access
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </a>
            <a 
              href="https://form.typeform.com/to/wbXoD5po"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white px-8 py-4 rounded-lg text-lg font-medium border border-white/30 hover:border-white/50 transition-colors inline-block"
            >
              Talk to Founder
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  )
} 